import React, { useState, useEffect, useRef } from "react";
import { motion, useAnimation, useInView } from "framer-motion";
import tree1 from "../images/olive2.jpg";
import tree2 from "../images/olive3.jpg";
import tree3 from "../images/olive5.jpg";
import tree4 from "../images/olive6.jpg";
import StoryMotif from "../images/StoryMotif.svg";
import { Helmet } from "react-helmet";

const Assest17 =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwMCIgaGVpZ2h0PSI4MDAiIHZpZXdCb3g9IjAgMCAxMjAwIDgwMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8ZGVmcz4KICAgIDxsaW5lYXJHcmFkaWVudCBpZD0iYmciIHgxPSIwJSIgeTE9IjAlIiB4Mj0iMTAwJSIgeTI9IjEwMCUiPgogICAgICA8c3RvcCBvZmZzZXQ9IjAlIiBzdG9wLWNvbG9yPSIjNGE2NzQxIi8+CiAgICAgIDxzdG9wIG9mZnNldD0iNTAlIiBzdG9wLWNvbG9yPSIjNmI4ZTVhIi8+CiAgICAgIDxzdG9wIG9mZnNldD0iMTAwJSIgc3RvcC1jb2xvcj0iIzJjM2UyOCIvPgogICAgPC9saW5lYXJHcmFkaWVudD4KICA8L2RlZnM+CiAgPHJlY3Qgd2lkdGg9IjEyMDAiIGhlaWdodD0iODAwIiBmaWxsPSJ1cmwoI2JnKSIvPgo8L3N2Zz4=";

const galleryImages = [
  { src: tree1, alt: "Olive Tree 1" },
  { src: tree2, alt: "Olive Tree 2" },
  { src: tree3, alt: "Olive Tree 3" },
  { src: tree4, alt: "Olive Tree 4" },
];

// Animated text component (unchanged)
const AnimatedText = ({ text, className, delay = 0, animation = "fade" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.1 });
  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const words = text.split(" ");
  const letters = text.split("");

  useEffect(() => {
    if (isInView && animation === "typewriter") {
      const timer = setTimeout(() => {
        if (currentIndex < letters.length) {
          setDisplayedText((prev) => prev + letters[currentIndex]);
          setCurrentIndex((prev) => prev + 1);
        }
      }, 30 + delay * 10);
      return () => clearTimeout(timer);
    }
  }, [isInView, currentIndex, letters, delay, animation]);

  if (animation === "typewriter") {
    return (
      <motion.div ref={ref} className={className}>
        {displayedText}
        <motion.span
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 0.8, repeat: Infinity }}
          className="ml-1 inline-block w-0.5 h-6 bg-white"
        />
      </motion.div>
    );
  }

  if (animation === "words") {
    return (
      <motion.div ref={ref} className={className}>
        {words.map((word, index) => (
          <motion.span
            key={index}
            initial={{ opacity: 0, y: 20, rotateX: -90 }}
            animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
            transition={{
              duration: 0.6,
              delay: delay + index * 0.1,
              ease: "easeOut",
            }}
            className="inline-block mr-2"
          >
            {word}
          </motion.span>
        ))}
      </motion.div>
    );
  }

  if (animation === "letters") {
    return (
      <motion.div ref={ref} className={className}>
        {letters.map((letter, index) => (
          <motion.span
            key={index}
            initial={{ opacity: 0, y: 50, scale: 0.3 }}
            animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{
              duration: 0.4,
              delay: delay + index * 0.02,
              type: "spring",
              stiffness: 100,
            }}
            className="inline-block"
          >
            {letter === " " ? "\u00A0" : letter}
          </motion.span>
        ))}
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay }}
      className={className}
    >
      {text}
    </motion.div>
  );
};

// Animated paragraph (unchanged)
const AnimatedParagraph = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.1 });
  const sentences = children.split(/(?<=[.!?])\s+/);

  return (
    <motion.div
      ref={ref}
      className="mt-4 text-base sm:text-lg md:text-base text-gray-300 leading-relaxed"
    >
      {sentences.map((sentence, index) => (
        <motion.span
          key={index}
          initial={{ opacity: 0, x: -20, filter: "blur(10px)" }}
          animate={
            isInView
              ? {
                  opacity: 1,
                  x: 0,
                  filter: "blur(0px)",
                }
              : {}
          }
          transition={{
            duration: 0.8,
            delay: delay + index * 0.3,
            ease: "easeOut",
          }}
          className="inline-block"
        >
          {sentence}
          {index < sentences.length - 1 ? " " : ""}
        </motion.span>
      ))}
    </motion.div>
  );
};

// ✅ Optimized AdvancedTextSection
const AdvancedTextSection = () => {
  const [activeImage, setActiveImage] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const loadedImagesRef = useRef(new Set());
  const titleRef = useRef(null);
  const isTitleInView = useInView(titleRef, { once: true });

  // ✅ Preload all images once (for instant switching)
  useEffect(() => {
    galleryImages.forEach(({ src }, i) => {
      const img = new Image();
      img.src = src;
      img.loading = "eager";
      img.decoding = "async";
      img.onload = () => loadedImagesRef.current.add(i);
    });
  }, []);

  // ✅ Auto change image
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImage((prev) => (prev + 1) % galleryImages.length);
      setIsImageLoading(true);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleImageChange = (direction) => {
    setActiveImage((prev) =>
      direction === "next"
        ? (prev + 1) % galleryImages.length
        : (prev - 1 + galleryImages.length) % galleryImages.length
    );
    setIsImageLoading(true);
  };

  const handleDotClick = (index) => {
    setActiveImage(index);
    setIsImageLoading(true);
  };

  return (
    <section
      className="relative pt-0 pb-12 overflow-hidden"
      id="about"
      style={{ backgroundColor: "#272e02" }}
    >
      <div className="absolute bottom-0 left-0 w-full overflow-hidden h-20 md:h-20">
        <svg
          className="w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
        >
          <path fill="#fff6e3" d="M -9 320 C 11 33 1409 331 1483 -4 L 1440 320 Z" />
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <motion.h2
            ref={titleRef}
            className="text-4xl md:text-5xl text-white tracking-tight font-serif italic relative inline-block overflow-hidden"
          >
            <motion.span
              initial={{ scaleX: 0, opacity: 0 }}
              animate={isTitleInView ? { scaleX: 1, opacity: 1 } : {}}
              transition={{ duration: 1.2, delay: 0.8 }}
              className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white to-transparent origin-left"
            >
              <motion.div
                animate={{ x: [-100, 100], opacity: [0, 1, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 1.5, ease: "easeInOut" }}
                className="w-8 h-full bg-yellow-400 rounded-full blur-sm"
              />
            </motion.span>
          </motion.h2>
        </motion.div>

        <div className="flex flex-col lg:flex-row items-start gap-14">
          {/* ✅ Optimized Image Section */}
          <div className="lg:w-1/2 w-full relative group">
            <div className="relative w-full h-[350px] sm:h-[400px] md:h-[400px] lg:h-[360px] overflow-hidden rounded-3xl shadow-2xl">
              {galleryImages.map((img, index) => (
                <img
                  key={index}
                  src={img.src}
                  alt={img.alt}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                    index === activeImage ? "opacity-100" : "opacity-0"
                  }`}
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                  onLoad={() => {
                    loadedImagesRef.current.add(index);
                    if (index === activeImage) setIsImageLoading(false);
                  }}
                  style={{
                    willChange: "opacity",
                    transform: "translateZ(0)",
                  }}
                />
              ))}

              <div className="absolute inset-0 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-all duration-500">
                <button
                  onClick={() => handleImageChange("prev")}
                  className="ml-4 p-3 bg-white/90 rounded-full shadow-xl"
                >
                  <svg
                    className="w-6 h-6 text-gray-800"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => handleImageChange("next")}
                  className="mr-4 p-3 bg-white/90 rounded-full shadow-xl"
                >
                  <svg
                    className="w-6 h-6 text-gray-800"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <div className="absolute bottom-6 left-0 right-0 flex justify-center">
                {galleryImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    className={`w-3 h-3 rounded-full mx-1 transition-all ${
                      index === activeImage ? "bg-white" : "bg-gray-400"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Text Section (unchanged) */}
          <motion.div
            initial={{ opacity: 0, y: 40, x: 50 }}
            whileInView={{ opacity: 1, y: 0, x: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            viewport={{ once: true }}
            className="lg:w-1/2 w-full lg:pl-4 relative"
          >
            <AnimatedText
              text="Our Story"
              animation="words"
              delay={0.3}
              className="text-2xl sm:text-2xl md:text-4xl font-[Nephilm] text-white"
            />

            <motion.div className="space-y-6 mt-6 font-[Inter18Light]">
              <AnimatedParagraph delay={0.8}>
                At Solivera, olive oil is not just a product. Itʼs a family tradition, a cultural identity, and a
                passionate craft passed down through generations.
              </AnimatedParagraph>

              <AnimatedParagraph delay={1.2}>
                Our olive trees grow in the ancient soils of Kairouan, Tunisia, where the warm sun, fertile land, and
                traditional know-how combine to produce an oil of unmatched flavor and purity.
              </AnimatedParagraph>

              <AnimatedParagraph delay={1.6}>
                Each harvest is performed with respect for the environment and a deep connection to our terroir. From
                the tree to the press, every step is guided by love, heritage, and the pursuit of excellence.
              </AnimatedParagraph>
            </motion.div>

            <img
              src={StoryMotif}
              alt="Motif"
              className="absolute top-0 left-1/2 -translate-x-1/2 w-64 sm:w-72 md:w-96 opacity-1 pointer-events-none -z-10"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AdvancedTextSection;
